// sticky-column.js — bi-directional sticky for a column TALLER than the viewport.
//
// THE PROBLEM. `position: sticky; top: 56px` assumes the element fits on screen. When it doesn't,
// it pins the moment its top reaches 56px and everything below the fold is unreachable until the
// whole container scrolls past — on a PDP that means the bottom of the info column is invisible
// until the (very tall) gallery finishes.
//
// THE BEHAVIOUR. Scroll down: the column rides up with the page until its BOTTOM meets the viewport
// bottom, then pins there. Scroll up: it rides down until its TOP meets the viewport top, then pins
// there. Nothing is ever unreachable, and there is no nested scrollbar for the visitor to discover.
//
// THE TRICK. There is no mode-switching and no scroll-direction branch. We keep CSS `position:
// sticky` and only rewrite its `top`, accumulating the frame's scroll delta into it and clamping:
//
//     top -= (scrollY - lastScrollY)         then clamp to [viewportH - elementH, maxTop]
//
// Scrolling down pushes `top` toward the negative floor (bottom-pinned); scrolling up pulls it back
// toward maxTop (top-pinned). Because the value only moves as fast as the page does, direction
// changes are seamless — the column resumes from wherever it is instead of jumping to a new anchor.
// The browser still does all the actual pinning, so we inherit its subpixel and containment
// behaviour rather than reimplementing it.
//
// maxTop is READ FROM CSS, not configured here: whatever `top` the stylesheet sets is the top-pinned
// offset. Re-read on resize so a per-breakpoint `top` still wins. Below the breakpoint the inline
// style is removed entirely and plain CSS sticky takes over.
//
// PROGRESSIVE ENHANCEMENT: with JS off, nothing sets `top` and you get ordinary CSS sticky — the
// current behaviour, no worse.
//
// USAGE: data-sticky-column on the element that is already `position: sticky` in CSS.
(function () {
  var MQ = '(min-width: 51.25rem)';

  function init(el) {
    if (el.__stickyColArmed) return;
    el.__stickyColArmed = true;

    var mq = window.matchMedia(MQ);
    var lastY = window.scrollY;
    var cur = 0; // the `top` we are currently applying, in px
    var maxTop = 0; // the CSS-authored top — read in measure()
    var ticking = false;

    // Read the stylesheet's own `top`. Must run while no inline top is set, or we would read our
    // own last write and the value would drift on every resize.
    function measure() {
      el.style.top = '';
      maxTop = parseFloat(window.getComputedStyle(el).top) || 0;
    }

    function update() {
      var y = window.scrollY;
      var delta = y - lastY;
      lastY = y;

      // Floor: how far up `top` may go before the column's bottom would rise past the viewport
      // bottom. Negative only when the column is taller than the viewport — when it fits, this
      // lands above maxTop and the clamp below holds it at maxTop, i.e. plain sticky.
      var floor = Math.min(maxTop, window.innerHeight - el.offsetHeight - maxTop);

      cur = Math.max(floor, Math.min(maxTop, cur - delta));
      el.style.top = cur + 'px';
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        update();
        ticking = false;
      });
    }

    function enable() {
      measure();
      cur = maxTop;
      lastY = window.scrollY;
      el.style.top = cur + 'px';
      window.addEventListener('scroll', onScroll, { passive: true });
    }

    function disable() {
      window.removeEventListener('scroll', onScroll);
      el.style.top = ''; // hand `top` back to the stylesheet
    }

    function sync() {
      if (mq.matches) {
        disable();
        enable();
      } else {
        disable();
      }
    }

    sync();
    // Re-measure on breakpoint changes so a per-breakpoint `top` is picked up.
    if (mq.addEventListener) mq.addEventListener('change', sync);
    else if (mq.addListener) mq.addListener(sync);

    window.addEventListener('resize', sync);

    // The column's height is NOT static: opening a product-details accordion grows it by a few
    // hundred px mid-scroll. Without this the floor would be stale and the newly revealed rows
    // would sit below the fold until the next scroll event re-clamped them.
    if (window.ResizeObserver) {
      new ResizeObserver(function () {
        if (mq.matches) update();
      }).observe(el);
    }
  }

  function scan(root) {
    if (!root || (root.nodeType !== 1 && root.nodeType !== 9)) return;
    if (root.matches && root.matches('[data-sticky-column]')) init(root);
    if (root.querySelectorAll) root.querySelectorAll('[data-sticky-column]').forEach(init);
  }

  scan(document);

  // Arms future instances too (theme-editor section re-render), same shape as wipe-line.js.
  new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var added = mutations[i].addedNodes;
      for (var j = 0; j < added.length; j++) scan(added[j]);
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
